import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, map, catchError, timeout } from 'rxjs';
import { AuthService } from '../services/auth';
import { PartnerKycService } from '../services/partner-kyc';
import { StorageService } from '../services/storage';
import { ToastService } from '../services/toast';

/** The KYC page itself is always reachable, otherwise a non-approved partner couldn't fix their KYC. */
const KYC_ROUTE = '/partner-kyc';
// Reachable regardless of KYC status (auth shell utilities).
const ALWAYS_ALLOWED = new Set<string>([KYC_ROUTE, '/forbidden', '/change-password']);

// Throttle the popup so a burst of redirects (the blocked nav + the landing nav) only
// alerts once. Reset whenever the partner actually reaches the KYC page.
let alertedFor = '';

/**
 * Gates the app behind an approved KYC. Runs after authGuard.
 *   - Admins are never gated.
 *   - Approved partners get the full app.
 *   - Everyone else is parked on the KYC page until an admin approves them — and gets an
 *     alert popup explaining why whenever they try to navigate elsewhere.
 */
export const kycGuard: CanActivateFn = (
  _route,
  state,
): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const kyc = inject(PartnerKycService);
  const storage = inject(StorageService);
  const toast = inject(ToastService);

  // SSR: no localStorage, let the browser re-run the guard after hydration.
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return true;

  // Admins are never gated.
  if (auth.isAdmin()) return true;

  const url = state.url.split('?')[0];

  const decide = (): boolean | UrlTree => {
    if (kyc.isApproved()) {
      alertedFor = '';
      return true; // full access
    }
    if (ALWAYS_ALLOWED.has(url)) {
      alertedFor = ''; // back on the KYC page — allow the next attempt to alert again
      return true; // let them reach/fix the KYC page
    }
    // Blocked: tell them why (once per blocked target), then park them on KYC.
    if (alertedFor !== url) {
      alertedFor = url;
      const { title, text } = kyc.blockedAlert();
      toast.popup(text, title, 'warning');
    }
    return router.parseUrl(KYC_ROUTE);
  };

  if (kyc.loaded()) return decide();

  // First protected navigation after a refresh: load KYC status once, then decide.
  // The timeout keeps a hung API from freezing navigation forever — a timeout error falls
  // into catchError's fail-safe branch (park on the KYC page) like any other failure.
  return kyc.loadMyKyc().pipe(
    timeout(10_000),
    map(() => decide()),
    catchError((err) => {
      // A 401 here means the session is dead, not that KYC is unknown. Parking the user on
      // the KYC page was actively wrong: errorInterceptor is concurrently navigating to
      // /login, this guard's UrlTree won the race, and the partner landed on a logged-in
      // looking KYC form with a dead token and a placeholder "User / Member" identity —
      // every field they filled in would have failed to save. Send them to /login instead,
      // so the two agree. Any OTHER failure (network, 5xx) still fails safe to KYC.
      if (err?.status === 401) {
        storage.clearAll();
        return of(router.parseUrl('/login'));
      }
      // The auth interceptor may have ended the session while we waited (failed refresh) —
      // the error here isn't a 401 then, but the session is gone all the same.
      if (!storage.getLocal('token')) {
        return of(router.parseUrl('/login'));
      }
      return of(ALWAYS_ALLOWED.has(url) ? true : router.parseUrl(KYC_ROUTE));
    }),
  );
};
